import React, { useRef, useState } from 'react';
import CircleButton from "../components/CircleButton";
import styled from 'styled-components';
import mic from 'microphone-stream';
import { toUtf8, fromUtf8 } from '@aws-sdk/util-utf8-node';
import { EventStreamMarshaller } from '@aws-sdk/eventstream-marshaller';
import FuzzySet from 'fuzzyset';
import Pickles from '../assets/img/pickles.png';
import ChantAudio1 from '../assets/audio/chant-1.mp3';
import ChantAudio2 from '../assets/audio/chant-2.mp3';
import ChantAudio3 from '../assets/audio/chant-3.mp3';
import MessageModal from '../components/MessageModal';

const ReadyHeading = styled.h1`
  font-family: 'Speedee Bold';
  font-size: 72px;
  line-height: 0.95em;
  letter-spacing: -0.03em;
  margin: 0;
  white-space: nowrap;

  > span {
    color: #FFBC0D;
  }

  em {
    margin-right: 12px;
  }
`;

const ChantText = styled.p`
  font-family: 'Speedee Bold';
  font-size: 33.82px;
  font-weight: bold;
  line-height: 0.95em;
  letter-spacing: -0.05em;
  margin: 48px 0 0 0;

   span {
    font-family: 'Speedee Bold';
    font-weight: bold;
    height: 36px;
    display: inline-block;
    padding-right: 4px;
    color: #2d2d2d;
  }
`;

const ButtonText = styled.p`
  position: absolute;
  top: 40px;
  font-family: 'Speedee Bold';
  font-size: 20px;
  font-weight: bold;
  line-height: 0.95em;
  letter-spacing: -0.05em;
  margin: 26px 0 0 0;
`;

const ListenChant = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none; 
  border-radius: 40px;
  background: #ebebeb;
  background-size: 100% 100%;
  padding: 4px 12px 4px 4px;
  font-size: 16px;
  line-height: 0.95;
  letter-spacing: -0.15px;
  margin: 24px 0 0 0;
  overflow-x: hidden;
  position: relative;
  color: #2D2D2D;

  svg {
    position: relative;
    z-index: 1;
    path {
      &.playing {
        fill: #fff;
      }
    }
  }

  span {
    position: relative;
    z-index: 1;
    &.playding {
      color: #fff;
    }
  }

  .dark-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #2d2d2d;
    transform: translateX(-100%);
    z-index: 0;
  }

  &.playing {
    svg {
      position: relative;
      z-index: 1;
      path {
        fill: #fff;
      }
    }

    span {
      position: relative;
      z-index: 1;
      color: #fff;
    }
  }
    
  @keyframes InProgress {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(0);
    }
  }
`;

const ReadyWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 56px);

  > img {
    &#img_pickles {
      position: absolute;
      top: -40px;
      right: -16px;
  }
`;

const StyledFooterPanel = styled.div`
  position: absolute;
  display: flex;
  bottom: 0;
  left: 50%;
  width: 748px;
  height: 420px;
  background: #FFBC0D;
  justify-content: center;
  border-radius: 50%;
  transform: translate3d(-50%, 50%, 0);
  transition: height .3s linear .1s;
  z-index: 1;

  > * {
    transform: translateY(-50%);
  }

  button, p {
    opacity: 1;
    transition: opacity 0.1s linear;
  }

  &.expanded {
    height: 300vh;

    button, p {
      opacity: 0;
    }
  }
`;

const audioFiles = [ChantAudio1, ChantAudio2, ChantAudio3];

function ReadyChant({ ...props }) {
  const { setScore, setIsReopeningSocket, setPage } = props;
  const sampleRate = 8000;
  const eventStreamMarshaller = new EventStreamMarshaller(toUtf8, fromUtf8);
  const chantAudioRef = useRef(null);
  const listenChantRef = useRef(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const footerPanelRef = useRef(null);
  const [modalDetails, setModalDetails] = useState(false);
  const [revealModal, setRevealModal] = useState(false);

  const getAudioEventMessage = buffer => {
    return {
      headers: {
        ':message-type': {
          type: 'string',
          value: 'event'
        },
        ':event-type': {
          type: 'string',
          value: 'AudioEvent'
        }
      },
      body: buffer
    };
  };

  const pcmEncode = input => {
    var offset = 0;
    var buffer = new ArrayBuffer(input.length * 2);
    var view = new DataView(buffer);

    for (var i = 0; i < input.length; i++, offset += 2) {
      var s = Math.max(-1, Math.min(1, input[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }

    return buffer;
  }

  const downsampleBuffer = (buffer, inputSampleRate = 44100, outputSampleRate = 16000) => {
    if (outputSampleRate === inputSampleRate) {
        return buffer;
    }

    var sampleRateRatio = inputSampleRate / outputSampleRate;
    var newLength = Math.round(buffer.length / sampleRateRatio);
    var result = new Float32Array(newLength);
    var offsetResult = 0;
    var offsetBuffer = 0;
    
    while (offsetResult < result.length) {
      var nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);

      var accum = 0,
      count = 0;
      
      for (var i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++ ) {
          accum += buffer[i];
          count++;
      }

      result[offsetResult] = accum / count;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }

    return result;
  }

  const handleEventStreamMessage = (messageJson) => {
    let results = messageJson.Transcript.Results;

    if (results.length > 0) {
      if (results[0]?.Alternatives.length > 0) {
        let transcript = results[0]?.Alternatives[0]?.Transcript;
        transcript = decodeURIComponent(escape(transcript));
        
        let fuzzyTest = FuzzySet();

        fuzzyTest.add('two all beef patties special sauce lettuce cheese pickles onions on a sesame seed bun');
        
        let transcriptResClean = transcript.toLowerCase().replace(/[^\w\s']|_/g, '');
        let fuzzyRes = fuzzyTest.get(transcriptResClean, false, 0);
        let currScore = fuzzyRes?.length > 0 && fuzzyRes[0]?.length > 0 && parseInt(fuzzyRes[0][0] * 100).toFixed(0);

        if (transcript.length >= 12) {
          setScore(prev => currScore > prev ? currScore : prev);
        }
      }
    }
  };

  const wireSocketEvents = () => {
    window.socket.onmessage = function (message) {
      let messageWrapper = eventStreamMarshaller.unmarshall(Buffer(message.data));
      let messageBody = JSON.parse(String.fromCharCode.apply(String, messageWrapper.body));
      
      if (messageWrapper.headers[":message-type"].value === "event") {
        handleEventStreamMessage(messageBody);
      }
      else {
        window.transcribeException = true;
        setModalDetails({
          title: 'Error',
          message: 'An error has been encountered. Please refresh the page and try again.',
          action: {
            label: 'Okay',
            refreshPage: true
          }
        });
        setRevealModal(true);
      }
    };

    window.socket.onerror = function () {
      window.socketError = true;
      setModalDetails({
        title: 'Error',
        message: 'An error has been encountered. Please refresh the page and try again.',
        action: {
          label: 'Okay',
          refreshPage: true
        }
      });
      setRevealModal(true);
    };
    
    window.socket.onclose = function (closeEvent) {
      if (!window.socketError && !window.transcribeException) {
        if (closeEvent.code !== 1000) {
          setModalDetails({
            title: 'Error',
            message: 'An error has been encountered. Please refresh the page and try again.',
            action: {
              label: 'Okay',
              refreshPage: true
            }
          });
          setRevealModal(true);
        }
      }
    };
  }

  const convertAudioToBinaryMessage = (audioChunk) => {
    let raw = mic.toRaw(audioChunk);

    if (raw == null)
        return;

    let downsampledBuffer = downsampleBuffer(raw, window.inputSampleRate, sampleRate);
    let pcmEncodedBuffer = pcmEncode(downsampledBuffer);

    let audioEventMessage = getAudioEventMessage(Buffer.from(pcmEncodedBuffer));

    let binary = eventStreamMarshaller.marshall(audioEventMessage);

    return binary;
  };

  window.closeSocket = () => {
    if (window.micStream) {
      window.micStream.stop();
    }

    if (window.socket.readyState === window.socket.OPEN) {
      let emptyMessage = getAudioEventMessage(Buffer.from(new Buffer([])));
      let emptyBuffer = eventStreamMarshaller.marshall(emptyMessage);

      window.socket.send(emptyBuffer);
    }
  };

  const streamAudioToWebSocket = async (userMediaStream) => {
    const token = await window.AwsWafIntegration.getToken();

    if (!token) {
      setModalDetails({
        title: 'Error',
        message: 'An error has been encountered. Please refresh the page and try again.',
        action: {
          label: 'Okay',
          refreshPage: true
        }
      });
      setRevealModal(true);

      return;
    } else {
      await fetch('https://5ghwfe8xpj.execute-api.ap-southeast-2.amazonaws.com/stg/', {
          headers: {
            'x-aws-waf-token': token
          }
        })
        .then(response => response.json())
        .then(data => {
          window.micStream = new mic();
          window.micStream.on("format", function(data) {
            window.inputSampleRate = data.sampleRate;
          });

          window.micStream.setStream(userMediaStream);

          window.socket = new WebSocket(data.body.url);
          window.socket.binaryType = "arraybuffer";

          window.socket.onopen = function() {
            setIsReopeningSocket(false);
            setPage('countdown');

            window.micStream.on('data', function(rawAudioChunk) {
              let binary = convertAudioToBinaryMessage(rawAudioChunk);

              if (window.socket.readyState === window.socket.OPEN)
                window.socket.send(binary);
            }
          )};

          wireSocketEvents();
        })
        .catch(error => {
          setModalDetails({
            title: 'Error',
            message: 'An error has been encountered. Please refresh the page and try again.',
            action: {
              label: 'Okay',
              refreshPage: true
            }
          });
          setRevealModal(true);
        });
    }
  };

  window.initRecording = async () => {
    window.gtag('event', 'chant_attempt_voice')
    window.navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream, setMicrophoneStream) => {
        streamAudioToWebSocket(stream, setMicrophoneStream)
        setPage('countdown');
      })
      .catch(error => {
        setModalDetails({
          title: 'Error',
          message: 'Permission to access microphone denied. Please refresh the page and allow access to the microphone.',
          action: {
            label: 'Okay',
            refreshPage: true
          }
        });
        setRevealModal(true);
      });
      footerPanelRef.current.classList.add('expanded');
  };

  const listenChant = () => {
    const audioPath = getRandomAudio();
    setCurrentAudio(audioPath);
    if (chantAudioRef.current) {
      chantAudioRef.current.load();
      chantAudioRef.current.play().catch(error => {
      setModalDetails({
        title: 'Error',
        message: 'Error playing audio. Please refresh the page and allow access to the microphone.',
        action: {
          label: 'Okay',
          refreshPage: true
        }
      });
      setRevealModal(true);
    });
    }
    chantAudioRef.current.onplaying = () => {
      const audioDuration = chantAudioRef.current.duration;
      listenChantRef.current.classList.add('playing');
      listenChantRef.current.disabled = true;
      listenChantRef.current.querySelector('.dark-bg').style.animation = `InProgress ${audioDuration}s linear backwards`;
    }
    chantAudioRef.current.onended = () => {
      listenChantRef.current.classList.remove('playing');
      listenChantRef.current.disabled = false;
      listenChantRef.current.querySelector('.dark-bg').style.animation = '';
    }
  }

  const getRandomAudio = () => {
    return audioFiles[Math.floor(Math.random() * audioFiles.length)];
  }
  
  return (
    <ReadyWrapper>
      <div>
        <ReadyHeading>Ready<br/>to <span><em>chant</em>?</span></ReadyHeading>
        <ListenChant onClick={listenChant} ref={listenChantRef} >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path d="M5.73828 17.416H8.22559C8.2959 17.416 8.36621 17.4336 8.41895 17.4863L12.2773 20.9316C12.7344 21.3447 13.0859 21.5381 13.5518 21.5381C14.2021 21.5381 14.6943 21.0459 14.6943 20.3955V6.97461C14.6943 6.31543 14.2021 5.78809 13.5342 5.78809C13.0859 5.78809 12.7695 5.99023 12.2773 6.43848L8.41895 9.85742C8.36621 9.91016 8.2959 9.92773 8.22559 9.92773H5.73828C4.4375 9.92773 3.77832 10.6133 3.77832 11.9844V15.3594C3.77832 16.7393 4.44629 17.416 5.73828 17.416ZM21.2246 19.9297C21.6289 20.1758 22.1211 20.0879 22.4023 19.6748C23.5449 18.0576 24.2129 15.8867 24.2129 13.6543C24.2129 11.4219 23.5537 9.24219 22.4023 7.63379C22.1211 7.2207 21.6289 7.12402 21.2246 7.37891C20.8027 7.64258 20.7412 8.16992 21.0576 8.63574C21.9893 10.0068 22.5254 11.791 22.5254 13.6543C22.5254 15.5088 21.9805 17.293 21.0576 18.6729C20.75 19.1387 20.8027 19.6572 21.2246 19.9297ZM5.89648 15.8428C5.60645 15.8428 5.46582 15.7021 5.46582 15.4121V11.9404C5.46582 11.6416 5.60645 11.501 5.89648 11.501H8.63867C8.87598 11.501 9.05176 11.457 9.25391 11.2725L12.752 8.08203C12.7959 8.04688 12.8398 8.02051 12.8926 8.02051C12.9629 8.02051 13.0156 8.06445 13.0156 8.14355V19.1738C13.0156 19.2617 12.9629 19.3057 12.9014 19.3057C12.8486 19.3057 12.8047 19.2881 12.752 19.2441L9.25391 16.0713C9.05176 15.8867 8.87598 15.8428 8.63867 15.8428H5.89648ZM17.6914 17.5742C18.0693 17.8203 18.5615 17.7324 18.834 17.3457C19.5459 16.3965 19.9678 15.0518 19.9678 13.6543C19.9678 12.2568 19.5459 10.9209 18.834 9.96289C18.5615 9.57617 18.0693 9.48828 17.6914 9.74316C17.2607 10.0244 17.1729 10.543 17.5244 11.0527C18.0078 11.7471 18.2803 12.6787 18.2803 13.6543C18.2803 14.6299 17.999 15.5527 17.5244 16.2559C17.1816 16.7656 17.2607 17.2842 17.6914 17.5742Z" fill="#2D2D2D"/>
          </svg>
          <span>Listen to the chant</span>   
          <div className='dark-bg'></div>
          <audio ref={chantAudioRef}>
            <source src={currentAudio} type="audio/mpeg"/>
            Your browser does not support the audio element.
          </audio>       
        </ListenChant>
        <ChantText><span>Two all-beef patties,<br/></span><span>special sauce,</span><br/><span>lettuce, cheese,</span><br/><span>pickles, onions on</span><br/><span>a sesame seed bun.</span></ChantText>
      </div>
      <img id="img_pickles" src={Pickles} alt="Pickles" />
      <StyledFooterPanel ref={footerPanelRef}>
        <CircleButton onClick={window.initRecording}>
          <svg width="24" height="35" viewBox="0 0 24 35" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.0004 22.5809C15.4056 22.5809 17.7861 20.0346 17.7861 16.3884V7.01674C17.7861 3.37054 15.4056 0.824219 12.0004 0.824219C8.61035 0.824219 6.2147 3.37054 6.2147 7.01674V16.3884C6.2147 20.0346 8.61035 22.5809 12.0004 22.5809ZM12.0004 19.7935C10.2376 19.7935 9.12263 18.4375 9.12263 16.4487V6.95647C9.12263 4.96763 10.2376 3.61161 12.0004 3.61161C13.7633 3.61161 14.8782 4.96763 14.8782 6.95647V16.4487C14.8782 18.4375 13.7633 19.7935 12.0004 19.7935ZM5.06961 34.0619H18.9312C19.7298 34.0619 20.3927 33.4141 20.3927 32.6306C20.3927 31.832 19.7298 31.1992 18.9312 31.1992H13.4016V28.1406C19.3531 27.5681 23.5116 23.3041 23.5116 17.0664V14.0982C23.5116 13.2997 22.8788 12.6819 22.0802 12.6819C21.2817 12.6819 20.6187 13.2997 20.6187 14.0982V16.9609C20.6187 22.0988 17.1383 25.4738 12.0004 25.4738C6.86258 25.4738 3.38211 22.0988 3.38211 16.9609V14.0982C3.38211 13.2997 2.73424 12.6819 1.93569 12.6819C1.13714 12.6819 0.489258 13.2997 0.489258 14.0982V17.0664C0.489258 23.3041 4.64774 27.5681 10.5992 28.1406V31.1992H5.06961C4.27107 31.1992 3.60812 31.832 3.60812 32.6306C3.60812 33.4141 4.27107 34.0619 5.06961 34.0619Z" fill="white"/>
          </svg>
        </CircleButton>
        <ButtonText>Record</ButtonText>
      </StyledFooterPanel>
      { revealModal && (
        <MessageModal details={modalDetails} setRevealModal={setRevealModal} />
      ) }
    </ReadyWrapper>
  );
}

export default ReadyChant;